import { Application, autoDetectRenderer, Container, Rectangle, Graphics} from 'pixi.js';
import {Waveform} from './waveform';
import {TimeIndicator} from './time-indicator';
import {
  TimelineTimestamp,
  PlayerControl,
} from 'storyboard-renderer';
import {Timebar} from './timebar';
import {MonoWaveform} from "./MonoWaveform";
import {Subject} from "rxjs/Subject";
import {TimelineService} from "../../../timeline.service";

export class StageRenderer extends Container {

  public backgroundContainer: Container;
  public waves: Container;
  public topWaveform: MonoWaveform;
  public timeIndicator: TimeIndicator;
  public timebar: Timebar;
  protected playerControl: PlayerControl;
  public timelinePositionGetter: Function;
  public timelinePositionSetter: Function;
  /**
   * a felhasználó draggeli-e épp a teljes timebar staget
   */
  protected dragging = false;
  /**
   * milyen széles a teljes renderer stage-je (tehát nem a canvas element, hanem benne a waveform)
   */
  public innerWidth: number;
  /**
   * milyen magas a canvas element
   */
  public outerHeight: number;

  constructor(waveData = null, playerControl:PlayerControl, innerWidth: number, outerHeight: number, timelineService, audioLength) {
    super();
    this.init(waveData, playerControl, innerWidth, outerHeight, timelineService, audioLength);
  }

  init(waveData = null, playerControl:PlayerControl, innerWidth: number, outerHeight: number, timelineService: TimelineService, audioLength) {
    this.removeChildren();

    this.setPlayerControl(playerControl);
    this.innerWidth = innerWidth;
    this.outerHeight = outerHeight;

    this.addChild(this.backgroundContainer = new Container());
    this.addChild(this.waves = new Container());

    if (waveData){
      this.topWaveform = MonoWaveform.fromJson(waveData);
      this.topWaveform.resample(timelineService.convertTimeToPx(audioLength.as_milliseconds));
      this.waves.addChild(this.topWaveform);
    }

    timelineService.zoomChangeEvent.subscribe((zoomLevel) => {
      if (this.topWaveform){
        this.topWaveform.resample(timelineService.convertTimeToPx(audioLength.as_milliseconds));
      }
    });

    this.timeIndicator = new TimeIndicator(playerControl, this.innerWidth, null);

    if (!this.topWaveform){
      /**
       * ha nincs topwaveform, akkor a timeindicator ne látszódjon
       *
       * az intance-t viszont muszáj megcsinálni belőle, mert használja a timebar
       */
      this.timeIndicator.alpha = 0;
    }

    this.timebar = this.createTimebar(this.timeIndicator);

    if (!this.topWaveform){
      this.timebar.position.y = 0;
    }else {
      this.timebar.position.y = 106;
    }

    let self = this;
    let waveContainerClickCallback = function(positionX, event){
      if (!self.dragging){
        let time = self.timeIndicator.getTimeFromIndicatorPosition(positionX);
        self.timeIndicator.moveTo(time);
      }
    }
    if (this.topWaveform){
      this.topWaveform.setWaveContainerClickCallback(waveContainerClickCallback);
    }

    this.addChild(this.timebar);

    this.addChild(this.timeIndicator);

    this.addTimelineDragEvents(this);
  }

  public setTimelinePositionGetter(value: Function){
    this.timelinePositionGetter = value;
  }

  public setTimelinePositionSetter(value: Function){
    this.timelinePositionSetter = value;
  }

  protected addTimelineDragEvents(stage: Container){
    let self = this;
    stage.interactive = true;
    stage.on('pointerdown', function(event){
      this._dragData = event.data;
      this.initialStagePosition = this.x;
      this.initialMousePositionFromStage = this._dragData.getLocalPosition(stage);
    });
    stage.on('pointermove', function(event){
      if (this._dragData){
        this.dragging = true;
        if (self.timelinePositionSetter){
          let mousePositionFromViewport = this._dragData.getLocalPosition(stage.parent);
          let distanceDraggedFromInitialStagePosition = this.initialMousePositionFromStage.x + this.initialStagePosition - mousePositionFromViewport.x;
          this.timelinePositionSetter(distanceDraggedFromInitialStagePosition * -1 + this.initialStagePosition);
        }
      }
    });
    let pointerUp = function(event){
      if (this._dragData){

      }
      this._dragData = null;
      this.dragging = false;
    };
    stage.on('pointerupoutside', pointerUp);
    stage.on('pointerup', pointerUp);
  }

  public ticker(delta?:number){

    if (this.topWaveform){
      this.recalculateTopWaveSizeAndPosition(this.topWaveform, this.innerWidth, this.outerHeight);
    }
    if (this.timebar){
      this.timebar.ticker.apply(this.timebar, [delta]);
    }
    if (this.timeIndicator && this.timeIndicator.ticker){
      this.timeIndicator.setMaxPositionX(this.innerWidth);
      this.timeIndicator.ticker.apply(this.timeIndicator, [delta]);

      this.setBackgroundToPosition(this.timeIndicator.getPosition().x);
    }
  }

  setPlayerControl(value:PlayerControl){
    this.playerControl = value;
  }

  getPlayerControl():PlayerControl{
    return this.playerControl;
  }

  public setSize(width: number, height: number){
    this.timeIndicator.setMaxPositionX(this.innerWidth);
  }

  public setInnerWidth(value: number){
    this.innerWidth = value;
  }

  protected setBackgroundToPosition(x:number){

    if (this["lastBackgroundPosition"] === x){
      return;
    }
    this["lastBackgroundPosition"] = x;

    if (this.backgroundContainer.children && this.backgroundContainer.children.length){
      this.backgroundContainer.children.map(function(child){
        child.destroy();
      });
    }
    this.backgroundContainer.removeChildren();
    this.backgroundContainer.addChild(this.createBackground(x));

    if (this.topWaveform){
      this.topWaveform.setHighlightedBars(x);
    }
    if (this.timebar){
      this.timebar.setHighlightedUntil(x);
    }
  }

  protected createBackground(width?:number):Graphics{
    let ret = new Graphics();
    ret.beginFill(0xbfe3ee);
    ret.drawRect(0, 0, width ? width : 0, this.outerHeight);
    return ret;
  }

  protected createTimebar(timeIndicator: TimeIndicator):Timebar{
    return new Timebar(this.timeIndicator);
  }

  protected recalculateTopWaveSizeAndPosition(waveform: MonoWaveform, totalWidth:number, totalHeight:number){
    const paddingTop = 15;
    const paddingBottom = 15 + 36;
    const paddingLeft = 0;
    const paddingRight = 0;
    waveform.outerWidth = totalWidth - paddingLeft - paddingRight;
    waveform.setOuterHeight((totalHeight - paddingTop - paddingBottom));
    if (waveform.outerWidth < 0) {
      waveform.outerWidth = 10;
    }
    if (waveform.outerHeight < 0) {
      waveform.setOuterHeight(10);
    }
    waveform.position.x = paddingLeft;
    waveform.position.y = paddingTop;
    waveform.recalculateWave();
  }
}



// WEBPACK FOOTER //
// ./src/app/storyboard/storyboard-edit/timeline/audio-track/waveform/waveform-renderer/stage-renderer.ts